import { divideRounded } from '@fd/money';
import { addMonths, daysBetween, daysInMonth, type IsoDate } from './dates.ts';
import { nextWorkingDay } from './workdays.ts';

// Loan balance with interest (DECISIONS D-023, D-029). Two ways to know a loan:
//
// - From the agreement's schedule (D-029): so many monthly payments from a first date, then
//   possibly one larger last payment (car finance: PCP, HP). The schedule alone says how many
//   payments are behind the owner on any day; what is owed is the remaining payments discounted
//   at the loan's rate — the capital plus the interest accrued so far, never the future interest
//   (the lender's "total still payable" is shown beside it). This is how UK lenders work out a
//   settlement figure, and it needs no repayments from the bank.
// - From the ledger (D-023): an opening balance and the repayments that reach the loan account,
//   with simple daily interest (APR / 365) charged at every repayment.
//
// Balances are negative (money owed), repayments positive.

export type LoanTerms = {
  /** Outstanding balance on the opening date (negative = owed). */
  readonly openingBalanceMinor: bigint;
  readonly openingDate: IsoDate;
  /** Annual rate in basis points (790 = 7.9 %); null = interest unknown, ledger only. */
  readonly interestRateBasisPoints: number | null;
  /** Monthly repayment the lender expects; used for the payoff projection. */
  readonly paymentMinor: bigint | null;
  readonly paymentDay: number | null;
  readonly originalPrincipalMinor: bigint | null;
  /** Regular monthly payments in the agreement (48); with the first date it fixes the schedule. */
  readonly paymentCount: number | null;
  /** One last payment a month after the regular ones (PCP's optional final payment, HP's last). */
  readonly finalPaymentMinor: bigint | null;
  /** Due date of the first regular payment. */
  readonly firstPaymentDate: IsoDate | null;
};

export type LoanRepayment = { readonly date: IsoDate; readonly amountMinor: bigint };

export type Instalment = {
  /** 1 for the first payment. */
  readonly number: number;
  /** The day the agreement names. */
  readonly dueDate: IsoDate;
  /** When the money leaves the bank: the next working day when the due date is not one. */
  readonly takenOn: IsoDate;
  readonly amountMinor: bigint;
  /** The last, larger payment. */
  readonly final: boolean;
};

export type LoanStatus = {
  /** Estimated amount still owed today (positive): with a schedule, capital plus accrued interest. */
  readonly outstandingMinor: bigint;
  readonly interestPaidMinor: bigint;
  readonly principalPaidMinor: bigint;
  readonly repaidCount: number;
  /** Share repaid, 0–10 000: of the original loan, or with a schedule of everything payable; null when unknown. */
  readonly progressBasisPoints: number | null;
  /** Projected with the usual repayment; null when it never pays off or terms are missing. */
  readonly payoffDate: IsoDate | null;
  readonly remainingPayments: number | null;
  readonly remainingInterestMinor: bigint | null;
  readonly nextPaymentDate: IsoDate | null;
  /** The agreement's schedule, when its terms are known; the fields below come from it. */
  readonly schedule: readonly Instalment[] | null;
  /** Payments in the schedule, the last one included. */
  readonly totalPayments: number | null;
  /** Everything still to pay by the schedule (the lender's "balance"), interest included. */
  readonly remainingPayableMinor: bigint | null;
  /** The day the loan began: a month before the first payment. */
  readonly startDate: IsoDate | null;
};

const YEAR_DAYS = 365n;
const AVERAGE_MONTH_DAYS = 365.25 / 12;

/** Interest on `owed` (positive) for `days` days at `rate` basis points a year. */
function interestFor(owed: bigint, days: number, rate: number): bigint {
  if (owed <= 0n || days <= 0 || rate <= 0) return 0n;
  return divideRounded(owed * BigInt(rate) * BigInt(days), 10_000n * YEAR_DAYS, 'HALF_EVEN');
}

function onDay(month: IsoDate, day: number): IsoDate {
  const [y, m] = month.split('-').map(Number) as [number, number];
  return `${month.slice(0, 8)}${String(Math.min(day, daysInMonth(y, m))).padStart(2, '0')}`;
}

/** First repayment date strictly after `after` on the loan's payment day. */
function nextOnPaymentDay(after: IsoDate, day: number): IsoDate {
  const same = onDay(after, day);
  return same > after ? same : onDay(addMonths(`${after.slice(0, 8)}01`, 1), day);
}

// ------------------------------------------------------------------------------ schedule

/** The agreement's payments, or null while its terms are incomplete. */
export function loanSchedule(terms: LoanTerms): Instalment[] | null {
  const first = terms.firstPaymentDate ?? null;
  const count = terms.paymentCount ?? null;
  const payment = terms.paymentMinor;
  if (!first || !count || count < 1 || !payment || payment <= 0n) return null;
  const day = terms.paymentDay ?? Number(first.slice(8, 10));
  const month = `${first.slice(0, 8)}01`;
  const list: Instalment[] = [];
  for (let k = 0; k < count; k++) {
    const dueDate = k === 0 ? first : onDay(addMonths(month, k), day);
    list.push({ number: k + 1, dueDate, takenOn: nextWorkingDay(dueDate), amountMinor: payment, final: false });
  }
  const last = terms.finalPaymentMinor ?? null;
  if (last !== null && last > 0n) {
    const dueDate = onDay(addMonths(month, count), day);
    list.push({ number: count + 1, dueDate, takenOn: nextWorkingDay(dueDate), amountMinor: last, final: true });
  }
  return list;
}

/** The day the loan began: "payments start one month after the date of this agreement". */
export function loanStartDate(terms: LoanTerms): IsoDate | null {
  const first = terms.firstPaymentDate ?? null;
  if (!first) return null;
  return onDay(addMonths(`${first.slice(0, 8)}01`, -1), terms.paymentDay ?? Number(first.slice(8, 10)));
}

/** Present value of the payments at month 0, at a monthly rate. */
function presentValue(amounts: readonly bigint[], rate: number): number {
  return amounts.reduce((sum, amount, i) => sum + Number(amount) / (1 + rate) ** (i + 1), 0);
}

/**
 * The loan's monthly rate: from the APR (an annual effective rate in the UK) when it is known,
 * otherwise the rate at which the payments repay the amount borrowed; 0 when neither is known.
 */
export function monthlyRate(terms: LoanTerms, schedule: readonly Instalment[]): number {
  if (terms.interestRateBasisPoints !== null) return (1 + terms.interestRateBasisPoints / 10_000) ** (1 / 12) - 1;
  const principal = terms.originalPrincipalMinor;
  const amounts = schedule.map((i) => i.amountMinor);
  if (!principal || principal <= 0n || presentValue(amounts, 0) <= Number(principal)) return 0;
  let low = 0;
  let high = 0.2;
  if (presentValue(amounts, high) > Number(principal)) return high;
  for (let n = 0; n < 100; n++) {
    const middle = (low + high) / 2;
    if (presentValue(amounts, middle) > Number(principal)) low = middle;
    else high = middle;
  }
  return (low + high) / 2;
}

/** Months since the start on `date`: whole months at the due dates, days in between. */
function monthsAt(date: IsoDate, start: IsoDate, schedule: readonly Instalment[]): number {
  if (date <= start) return 0;
  let previous = start;
  for (let k = 0; k < schedule.length; k++) {
    const due = schedule[k]!.dueDate;
    if (date < due) return k + daysBetween(previous, date) / daysBetween(previous, due);
    previous = due;
  }
  return schedule.length + daysBetween(previous, date) / AVERAGE_MONTH_DAYS;
}

/** Unpaid payments discounted to `date`: what settling the loan that day would cost. */
function owedOn(date: IsoDate, start: IsoDate, schedule: readonly Instalment[], rate: number): number {
  const now = monthsAt(date, start, schedule);
  return schedule.filter((i) => i.takenOn >= date).reduce((sum, i) => sum + Number(i.amountMinor) * (1 + rate) ** (now - i.number), 0);
}

function scheduledStatus(terms: LoanTerms, schedule: readonly Instalment[], today: IsoDate): LoanStatus {
  const start = loanStartDate(terms)!;
  const rate = monthlyRate(terms, schedule);
  // Taken on the day it is due counts from the day after: until then it is "due today".
  const paid = schedule.filter((i) => i.takenOn < today);
  const unpaid = schedule.filter((i) => i.takenOn >= today);
  const paidMinor = paid.reduce((sum, i) => sum + i.amountMinor, 0n);
  const remainingPayable = unpaid.reduce((sum, i) => sum + i.amountMinor, 0n);
  const total = paidMinor + remainingPayable;
  const outstanding = BigInt(Math.round(owedOn(today, start, schedule, rate)));
  // Capital repaid: the loan at the start less the capital left just after the last payment.
  const borrowed = presentValue(schedule.map((i) => i.amountMinor), rate);
  const lastNumber = paid.at(-1)?.number ?? 0;
  const capitalLeft = unpaid.reduce((sum, i) => sum + Number(i.amountMinor) / (1 + rate) ** (i.number - lastNumber), 0);
  const principalPaid = BigInt(Math.max(0, Math.round(borrowed - capitalLeft)));
  const principalPaidMinor = principalPaid > paidMinor ? paidMinor : principalPaid;
  return {
    outstandingMinor: outstanding,
    interestPaidMinor: paidMinor - principalPaidMinor,
    principalPaidMinor,
    repaidCount: paid.length,
    progressBasisPoints: total > 0n ? Number((paidMinor * 10_000n) / total) : null,
    payoffDate: schedule.at(-1)!.dueDate,
    remainingPayments: unpaid.length,
    remainingInterestMinor: remainingPayable > outstanding ? remainingPayable - outstanding : 0n,
    nextPaymentDate: unpaid[0]?.takenOn ?? null,
    schedule,
    totalPayments: schedule.length,
    remainingPayableMinor: remainingPayable,
    startDate: start,
  };
}

/** What a scheduled loan cost to settle on `date` (positive); null without a schedule or before it began. */
export function scheduledLoanBalance(terms: LoanTerms, date: IsoDate): bigint | null {
  return scheduledLoanBalances(terms)(date);
}

/**
 * The same for many days (a net worth chart asks for a hundred): the schedule and its rate, found by
 * iterating, are worked out once instead of on every day.
 */
export function scheduledLoanBalances(terms: LoanTerms): (date: IsoDate) => bigint | null {
  const schedule = loanSchedule(terms);
  const start = loanStartDate(terms);
  if (!schedule || !start) return () => null;
  const rate = monthlyRate(terms, schedule);
  return (date) => (date < start ? null : BigInt(Math.round(owedOn(date, start, schedule, rate))));
}

export type LoanWhatIf = {
  /** The day the last payment is made. */
  readonly payoffDate: IsoDate;
  /** Payments still to make, the last one included. */
  readonly payments: number;
  /** Everything still to pay: regular payments, extras and the last payment. */
  readonly totalPaidMinor: bigint;
  /** Of that, interest (what is paid beyond what is owed today). */
  readonly interestMinor: bigint;
  /** The last payment: the agreement's final one, or the part of a regular one that clears the loan. */
  readonly lastPaymentMinor: bigint;
};

/**
 * Pays a scheduled loan from today with extra money on top: every month more, and once a larger
 * sum. Interest runs at the loan's monthly rate from one due date to the next; an extra lowers what
 * the interest runs on from its day. The loan ends when a payment clears it, or with the last
 * payment of the agreement taking what is left. Without extras it is the agreement itself.
 */
export function loanWhatIf(terms: LoanTerms, today: IsoDate, extras: { monthlyMinor?: bigint; oneOffMinor?: bigint; oneOffDate?: IsoDate | null } = {}): LoanWhatIf | null {
  const schedule = loanSchedule(terms);
  const start = loanStartDate(terms);
  if (!schedule || !start) return null;
  const rate = monthlyRate(terms, schedule);
  const unpaid = schedule.filter((i) => i.takenOn >= today);
  if (unpaid.length === 0) return { payoffDate: schedule.at(-1)!.dueDate, payments: 0, totalPaidMinor: 0n, interestMinor: 0n, lastPaymentMinor: 0n };
  const owedToday = owedOn(today, start, schedule, rate);
  const monthly = Number(extras.monthlyMinor ?? 0n);
  const oneOff = Number(extras.oneOffMinor ?? 0n);
  let oneOffDone = oneOff <= 0 || !extras.oneOffDate;
  let balance = owedToday;
  let from = monthsAt(today, start, schedule);
  let paid = 0;
  let payments = 0;
  for (const instalment of unpaid) {
    // A one-off extra before this payment stops interest on it from its own day.
    if (!oneOffDone && extras.oneOffDate! < instalment.dueDate) {
      const at = Math.max(from, monthsAt(extras.oneOffDate!, start, schedule));
      balance = balance * (1 + rate) ** (at - from);
      from = at;
      const part = Math.min(oneOff, balance);
      balance -= part;
      paid += part;
      oneOffDone = true;
    }
    balance = balance * (1 + rate) ** (instalment.number - from);
    from = instalment.number;
    const due = instalment.final ? balance : Number(instalment.amountMinor) + monthly;
    const pay = Math.min(due, balance);
    balance -= pay;
    paid += pay;
    payments += 1;
    if (balance <= 0.5) {
      return {
        payoffDate: instalment.dueDate,
        payments,
        totalPaidMinor: BigInt(Math.round(paid)),
        interestMinor: BigInt(Math.max(0, Math.round(paid - owedToday))),
        lastPaymentMinor: BigInt(Math.round(pay)),
      };
    }
  }
  // No final payment in the agreement and the extras never caught up: what is left is due at the end.
  const last = unpaid.at(-1)!;
  return {
    payoffDate: last.dueDate,
    payments,
    totalPaidMinor: BigInt(Math.round(paid + balance)),
    interestMinor: BigInt(Math.max(0, Math.round(paid + balance - owedToday))),
    lastPaymentMinor: BigInt(Math.round(balance)),
  };
}

/** A scheduled loan's balance at each of its due dates from the start (for the chart). */
export function loanBalanceSeries(terms: LoanTerms): { date: IsoDate; balanceMinor: bigint }[] {
  const schedule = loanSchedule(terms);
  const start = loanStartDate(terms);
  if (!schedule || !start) return [];
  const rate = monthlyRate(terms, schedule);
  const series = [{ date: start, balanceMinor: BigInt(Math.round(owedOn(start, start, schedule, rate))) }];
  for (const instalment of schedule) {
    // Just after the payment: what is still owed then.
    const after = schedule.filter((i) => i.number > instalment.number).reduce((sum, i) => sum + Number(i.amountMinor) / (1 + rate) ** (i.number - instalment.number), 0);
    series.push({ date: instalment.dueDate, balanceMinor: BigInt(Math.round(after)) });
  }
  return series;
}

// ------------------------------------------------------------------------------ status

export function loanStatus(terms: LoanTerms, repayments: readonly LoanRepayment[], today: IsoDate): LoanStatus {
  const schedule = loanSchedule(terms);
  if (schedule) return scheduledStatus(terms, schedule, today);

  const rate = terms.interestRateBasisPoints ?? 0;
  let owed = terms.openingBalanceMinor < 0n ? -terms.openingBalanceMinor : 0n;
  let last = terms.openingDate;
  let interestPaid = 0n;
  let principalPaid = 0n;
  let count = 0;
  const sorted = repayments
    .filter((r) => r.date >= terms.openingDate && r.date <= today && r.amountMinor > 0n)
    .sort((a, b) => (a.date < b.date ? -1 : a.date > b.date ? 1 : 0));
  for (const repayment of sorted) {
    const interest = interestFor(owed, daysBetween(last, repayment.date), rate);
    owed += interest;
    const paid = repayment.amountMinor > owed ? owed : repayment.amountMinor;
    owed -= paid;
    interestPaid += interest < paid ? interest : paid;
    principalPaid += paid > interest ? paid - interest : 0n;
    last = repayment.date;
    count += 1;
  }

  const original = terms.originalPrincipalMinor;
  const progress = original && original > 0n ? Math.max(0, Math.min(10_000, Number(((original - owed) * 10_000n) / original))) : null;

  let payoffDate: IsoDate | null = null;
  let remainingPayments: number | null = null;
  let remainingInterest: bigint | null = null;
  let nextPaymentDate: IsoDate | null = null;
  if (terms.paymentDay !== null) nextPaymentDate = nextOnPaymentDay(sorted.length > 0 ? last : today < terms.openingDate ? terms.openingDate : today, terms.paymentDay);
  if (owed === 0n) {
    payoffDate = last;
    remainingPayments = 0;
    remainingInterest = 0n;
    nextPaymentDate = null;
  } else if (terms.paymentMinor && terms.paymentMinor > 0n && terms.paymentDay !== null && nextPaymentDate) {
    // Project month by month; give up after 50 years (the repayment does not cover the interest).
    let balance = owed;
    let from = last;
    let date = nextPaymentDate;
    let interestSum = 0n;
    for (let n = 1; n <= 600; n++) {
      const interest = interestFor(balance, daysBetween(from, date), rate);
      balance += interest;
      interestSum += interest;
      if (balance <= terms.paymentMinor) {
        payoffDate = date;
        remainingPayments = n;
        remainingInterest = interestSum;
        break;
      }
      balance -= terms.paymentMinor;
      from = date;
      date = onDay(addMonths(`${date.slice(0, 8)}01`, 1), terms.paymentDay);
    }
  }

  return {
    outstandingMinor: owed,
    interestPaidMinor: interestPaid,
    principalPaidMinor: principalPaid,
    repaidCount: count,
    progressBasisPoints: progress,
    payoffDate,
    remainingPayments,
    remainingInterestMinor: remainingInterest,
    // The bank takes it on the next working day.
    nextPaymentDate: nextPaymentDate ? nextWorkingDay(nextPaymentDate) : null,
    schedule: null,
    totalPayments: null,
    remainingPayableMinor: null,
    startDate: null,
  };
}
